import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import useBoopMinimal from '@/hooks/use-boop-minimal';

import Space from '@/components/Space';

const STRETCH_AMOUNT = 3;

function StretchyArrowComparisonDemo() {
  return (
    <Row>
      <Col>
        <Heading>Without Squeeze</Heading>
        <Arrow shouldSqueeze={false} />
      </Col>
      <Col>
        <Heading>With Squeeze</Heading>
        <Arrow shouldSqueeze={true} />
      </Col>
    </Row>
  );
}

function Arrow({ shouldSqueeze }: { shouldSqueeze: boolean }) {
  const [isHovering, setIsHovering] = React.useState(false);
  const isBooped = useBoopMinimal(isHovering, {
    ignoreMotionPreference: true,
  });

  const lineProps = useSpring({
    x2: LINE_EXTENDS_TO[isBooped ? 'booped' : 'default'],
    config: {
      tension: 300,
      friction: 16,
    },
  });
  const polyLineProps = useSpring({
    points:
      POINTS[
        isBooped
          ? shouldSqueeze
            ? 'squeezed'
            : 'regular'
          : 'default'
      ],
    config: {
      tension: 300,
      friction: 16,
    },
  });

  return (
    <ArrowBtn
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchStart={() => setIsHovering(true)}
      onTouchEnd={() => setIsHovering(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          setIsHovering(true);
        }
      }}
      onKeyUp={(event) => {
        if (event.key === 'Enter') {
          setIsHovering(false);
        }
      }}
    >
      <Svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animated.line x1="5" y1="12" y2="12" {...lineProps} />
        <animated.polyline {...polyLineProps} />
      </Svg>
    </ArrowBtn>
  );
}

const POINTS = {
  default: '12 5 19 12 12 19',
  squeezed: `${12 + STRETCH_AMOUNT} 7 ${19 + STRETCH_AMOUNT} 12 ${
    12 + STRETCH_AMOUNT
  } 17`,
  regular: `${12 + STRETCH_AMOUNT} 5 ${19 + STRETCH_AMOUNT} 12 ${
    12 + STRETCH_AMOUNT
  } 19`,
};
const LINE_EXTENDS_TO = {
  default: 18,
  booped: 18 + STRETCH_AMOUNT,
};

const Row = styled(Space)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 32px;
  gap: 32px;
  border: var(--page-border);
  border-radius: 8px;
  margin-bottom: 64px;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Heading = styled.p`
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  text-align: center;
  margin-bottom: 0;
`;

const ArrowBtn = styled.button`
  padding: 12px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  -webkit-tap-highlight-color: transparent;
`;

const Svg = styled.svg`
  display: block;
  transition: opacity 250ms;
  width: 128px;
  height: 128px;
  overflow: visible;
  fill: none;
  stroke: white;
  stroke-width: 2px;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

export default StretchyArrowComparisonDemo;
